import React, { useState, useEffect, useCallback } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { HiMenuAlt2, HiOutlineShoppingBag, HiOutlineSparkles, HiX } from 'react-icons/hi'
import { logoutData, SidebarData, SidebarDataNewUser } from './SidebarData'
import './Navbar.css'
import brand from '../brand'
import AuthenticationService from '../service/AuthenticationService'
import CartService from '../service/CartService';
import ProfileService from '../service/ProfileService';
import AiService from '../service/AiService';
import MonitorService, { SERVICES_CHANGED } from '../service/MonitorService';
import { CART_UPDATED_EVENT } from '../util/cartEvents'
import { AUTH_CHANGED_EVENT, notifyAuthChanged } from '../util/authEvents'


const Navbar = () => {

    const location = useLocation();
    const history = useHistory();

    const [sidebar, setSidebar] = useState(false)
    const [isLoggedIn, setIsLoggedIn] = useState(false)
    const [userName, setUserName] = useState(false)
    const [isAdmin, setIsAdmin] = useState(false)
    const [role, setRole] = useState('USER')
    const [canManageSettings, setCanManageSettings] = useState(false)
    const [active, setActive] = useState(false)
    const [, setUserType] = useState(false)
    const [cartCount, setCartCount] = useState(0)
    const [cartError, setCartError] = useState('')
    const [aiOn, setAiOn] = useState(() => AiService.cachedEnabled())
    const [serviceWarnings, setServiceWarnings] = useState(0)

    const showCart = active && !isAdmin

    // AI Help shows only for shoppers while an admin has it switched on (remembered for the session)
    useEffect(() => {
        if (!showCart) return
        let alive = true
        AiService.enabled().then(on => { if (alive) setAiOn(on) })
        return () => { alive = false }
    }, [showCart])

    // Admins: how many outside services need attention (menu badge). Never for managers: a 403 signs them out.
    useEffect(() => {
        if (!canManageSettings) {
            setServiceWarnings(0)
            return
        }
        let alive = true
        const load = () => MonitorService.warnings()
            .then(res => { if (alive) setServiceWarnings(Number(res.data.data.count) || 0) })
            .catch(() => { })
        const onChanged = (e) => setServiceWarnings(Number(e.detail) || 0)
        load()
        const timer = setInterval(load, 10 * 60 * 1000)
        window.addEventListener(SERVICES_CHANGED, onChanged)
        return () => { alive = false; clearInterval(timer); window.removeEventListener(SERVICES_CHANGED, onChanged) }
    }, [canManageSettings])

    // The /logout page signs out and shows a confirmation.
    function logout() {
        setSidebar(false)
        history.push('/logout')
    }

    // Back to the guest menu when the user signs out (fired by the /logout page).
    useEffect(() => {
        const onAuthChanged = () => {
            if (AuthenticationService.isUserLoggedIn()) return
            setIsLoggedIn(false)
            setUserName(false)
            setIsAdmin(false)
            setRole('USER')
            setCanManageSettings(false)
            setActive(false)
            setCartCount(0)
        }
        window.addEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
        return () => window.removeEventListener(AUTH_CHANGED_EVENT, onAuthChanged)
    }, [])

    const hideNav = () => {
        setSidebar(false)
    }

    // Close the drawer with Escape.
    useEffect(() => {
        if (!sidebar) return
        const onKey = (e) => { if (e.key === 'Escape') setSidebar(false) }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    }, [sidebar])

    // Sync auth state from localStorage / backend on every navigation.
    useEffect(() => {
        // /logout clears the session itself; syncing here could race with it and restore the user.
        if (location.pathname === '/logout') return
        if (AuthenticationService.getAuthUserName() !== null) {
            let bl = AuthenticationService.getIsAdmin() === "true";
            setUserName(AuthenticationService.getAuthUserName());
            setIsAdmin(bl);
            setRole(AuthenticationService.getRole())
            setCanManageSettings(AuthenticationService.canManageSettings())
            setIsLoggedIn(AuthenticationService.getAuthUserName() !== null)
            let b2 = AuthenticationService.getLocalStroageByKey('hasProfile') === "true";
            setActive(b2)
            setUserType(AuthenticationService.getLocalStroageByKey('userType'))
        }

        if (AuthenticationService.queryData()) {

            AuthenticationService.getUser().then(response => {
                // ignore a reply that arrives after the user signed out
                if (!AuthenticationService.isUserLoggedIn()) return
                AuthenticationService.updateAuthData(response.data.userName, response.data.isAdmin, response.data.hasProfile,
                    response.data.userType, response.data.role, response.data.canManageSettings)
                // route guards (ShopperOnly / StaffOnly) re-check the role
                notifyAuthChanged()
                setUserName(AuthenticationService.getAuthUserName());
                let bl = AuthenticationService.getIsAdmin() === "true";
                setIsAdmin(bl);
                setRole(AuthenticationService.getRole())
                setCanManageSettings(AuthenticationService.canManageSettings())
                setIsLoggedIn(AuthenticationService.getAuthUserName() !== null)
                let b2 = AuthenticationService.getLocalStroageByKey('hasProfile') === "true";
                setActive(b2)
                setUserType(response.data.userType)
            }).catch(error => {
                console.log(error)
                AuthenticationService.logout()
                setIsLoggedIn(false)
                notifyAuthChanged()
            });
        }
    }, [location.pathname]);

    // While signed in, re-check the account every minute and when the tab comes back into view.
    // If an admin disabled it, the request gets 401 and axiosInstance signs the user out.
    useEffect(() => {
        if (!isLoggedIn) return
        const check = () => {
            if (AuthenticationService.isUserLoggedIn()) AuthenticationService.getUser().catch(() => {})
        }
        const onVisible = () => { if (document.visibilityState === 'visible') check() }
        const timer = setInterval(check, 60000)
        document.addEventListener('visibilitychange', onVisible)
        return () => {
            clearInterval(timer)
            document.removeEventListener('visibilitychange', onVisible)
        }
    }, [isLoggedIn])

    const refreshCartCount = useCallback(() => {
        CartService.getCartItems().then(response => {
            const items = response?.data?.data?.cartListView
            setCartCount(Array.isArray(items) ? items.length : 0)
        }).catch(() => setCartCount(0))
    }, [])

    useEffect(() => {
        if (!showCart) return
        refreshCartCount()
        window.addEventListener(CART_UPDATED_EVENT, refreshCartCount)
        return () => window.removeEventListener(CART_UPDATED_EVENT, refreshCartCount)
    }, [showCart, location.pathname, refreshCartCount])

    const redirectConfirmationPage = () => {
        setCartError('')
        ProfileService.isUserConfirmed()
            .then((response) => {
                if (response?.status === 200) {
                    history.push({
                        pathname: '/cart',
                    });
                }
            })
            .catch((error) => {
                if (error?.response?.status === 401) {
                    history.push({
                        pathname: '/login',
                        state: { id: '/shop/items/view/' },
                    });
                } else if (error?.response?.status === 400) {
                    history.push({
                        pathname: '/profile'
                    });
                } else {
                    setCartError('Could not open your cart. Please try again.');
                }
            });
    };

    const menuItems = active
        ? SidebarData.filter(item => (item.user === !isAdmin || item.default === true) && (!item.adminOnly || canManageSettings)
            && (!item.aiHelp || aiOn))
        : SidebarDataNewUser

    const isCurrent = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path)

    return (
        <>
            <header className='mv-header'>
                <div className='mv-header-inner'>
                    <button
                        type='button'
                        className='mv-icon-btn'
                        aria-label='Open menu'
                        aria-expanded={sidebar}
                        aria-controls='mv-drawer'
                        onClick={() => setSidebar(true)}
                    >
                        <HiMenuAlt2 />
                    </button>

                    <Link to={active && isAdmin ? '/dashboard' : '/'} className={'mv-header-logo' + (brand.logoCrop ? ' is-cropped' : '')} aria-label={brand.name + ' home'}>
                        <img src={brand.logo} alt={brand.fullName} />
                    </Link>

                    {showCart ?
                        <div className='mv-header-actions'>
                            {aiOn &&
                                <Link to='/help' className={'mv-icon-btn mv-ai-header-btn' + (location.pathname === '/help' ? ' current' : '')}
                                    aria-label='AI Help' title='AI Help' aria-current={location.pathname === '/help' ? 'page' : undefined}>
                                    <HiOutlineSparkles />
                                </Link>}
                            <button type='button' className='mv-icon-btn' aria-label={`Cart, ${cartCount} items`} onClick={redirectConfirmationPage}>
                                <HiOutlineShoppingBag />
                                {cartCount > 0 && <span className='mv-cart-badge'>{cartCount > 9 ? '9+' : cartCount}</span>}
                            </button>
                        </div>
                        :
                        <span className='mv-header-spacer' aria-hidden='true' />
                    }
                </div>
                {cartError && <div className='mv-alert mv-alert-error mv-header-alert' role='alert'>{cartError}</div>}
            </header>

            <div className={sidebar ? 'mv-drawer-overlay open' : 'mv-drawer-overlay'} onClick={hideNav} aria-hidden='true' />

            <nav id='mv-drawer' className={sidebar ? 'mv-drawer open' : 'mv-drawer'} aria-label='Main menu' aria-hidden={!sidebar}>
                <div className='mv-drawer-head'>
                    <img src={brand.logo} alt='' className={'mv-drawer-logo' + (brand.logoCrop ? ' is-cropped' : '')} />
                    <button type='button' className='mv-icon-btn' aria-label='Close menu' onClick={hideNav} tabIndex={sidebar ? 0 : -1}>
                        <HiX />
                    </button>
                </div>

                {isLoggedIn && userName &&
                    <div className='mv-drawer-user'>
                        <span className='mv-drawer-avatar' aria-hidden='true'>{String(userName).charAt(0).toUpperCase()}</span>
                        <span>
                            <span className='mv-drawer-hello'>Hello,</span>
                            <span className='mv-drawer-name'>{userName}</span>
                            {isAdmin && <span className='mv-role-badge'>{role === 'MANAGER' ? 'Manager' : 'Admin'}</span>}
                        </span>
                    </div>
                }

                <ul className='mv-drawer-list'>
                    {menuItems.map((item, index) => (
                        <li key={index}>
                            <Link
                                to={item.path}
                                onClick={hideNav}
                                className={isCurrent(item.path) ? 'mv-drawer-link current' : 'mv-drawer-link'}
                                aria-current={isCurrent(item.path) ? 'page' : undefined}
                                tabIndex={sidebar ? 0 : -1}
                            >
                                <span className='mv-drawer-icon'>{item.icon}</span>
                                <span>{item.title}</span>
                                {item.badge && <span className='mv-drawer-badge'>{item.badge}</span>}
                                {item.serviceWarnings && serviceWarnings > 0 &&
                                    <span className='mv-drawer-badge is-warn' aria-label={`${serviceWarnings} need attention`}>{serviceWarnings}</span>}
                            </Link>
                        </li>
                    ))}
                    {active &&
                        <li key={logoutData.index} className='mv-drawer-logout'>
                            <button type='button' className='mv-drawer-link' onClick={logout} tabIndex={sidebar ? 0 : -1}>
                                <span className='mv-drawer-icon'>{logoutData.icon}</span>
                                <span>{logoutData.title}</span>
                            </button>
                        </li>
                    }
                </ul>
            </nav>
        </>
    )
}

export default Navbar
